import { useState } from 'react';
import {
  deleteContract,
  deleteContractFile,
  updateContractFile,
} from './contractDetailApi';

function showContractDeletedToast(data, showToast) {
  if (data.driveWarning) showToast(data.driveWarning, 'warning');
  else if (data.driveFolderTrashed && data.counterpartyFolderTrashed) {
    showToast('Contract deleted; Drive folder + empty counterparty folder moved to Trash', 'success');
  } else if (data.driveFolderTrashed) {
    showToast('Contract deleted; Drive folder moved to Trash', 'success');
  } else {
    showToast('Contract deleted', 'success');
  }
}

export default function useContractFileActions({
  contractFiles,
  contractId,
  navigate,
  refreshContract,
  showToast,
}) {
  const [fileDeleteTarget, setFileDeleteTarget] = useState(null);
  const [deleteDriveFile, setDeleteDriveFile] = useState(true);
  const [deleteContractWithLastFile, setDeleteContractWithLastFile] = useState(false);
  const [deleteContractDriveFolderWithLastFile, setDeleteContractDriveFolderWithLastFile] = useState(true);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [markingFileId, setMarkingFileId] = useState(null);
  const [editingFileId, setEditingFileId] = useState(null);
  const [fileDraft, setFileDraft] = useState({ documentDate: '', direction: '', fileNotes: '' });
  const [savingFileMeta, setSavingFileMeta] = useState(false);

  const openFileDeleteConfirm = (file) => {
    setFileDeleteTarget({
      ...file,
      isOnlyFile: contractFiles.length === 1,
    });
    setDeleteDriveFile(!!file.driveFileId);
    setDeleteContractWithLastFile(false);
    setDeleteContractDriveFolderWithLastFile(true);
  };

  const closeFileDeleteConfirm = () => {
    if (deletingFileId) return;
    setFileDeleteTarget(null);
    setDeleteDriveFile(true);
    setDeleteContractWithLastFile(false);
    setDeleteContractDriveFolderWithLastFile(true);
  };

  const handleDeleteFile = async () => {
    if (!fileDeleteTarget?.fileId || deletingFileId) return;
    const fileId = fileDeleteTarget.fileId;
    setDeletingFileId(fileId);
    try {
      if (fileDeleteTarget.isOnlyFile && deleteContractWithLastFile) {
        const data = await deleteContract(contractId, {
          deleteDriveFolder: deleteContractDriveFolderWithLastFile,
        }) || {};
        showContractDeletedToast(data, showToast);
        navigate('/contracts');
        return;
      }

      const data = await deleteContractFile(contractId, fileId, { deleteDriveFile }) || {};
      if (data.driveWarning) showToast(data.driveWarning, 'warning');
      else if (data.driveFileTrashed) showToast('File removed; Drive copy moved to Trash', 'success');
      else showToast('File removed from contract', 'success');
      if (data.localWarning) showToast(data.localWarning, 'warning');
      setFileDeleteTarget(null);
      setDeleteDriveFile(true);
      refreshContract();
    } catch (err) {
      showToast(err.message || 'Failed to delete file', 'error');
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleMarkAsExecuted = async (fileId) => {
    if (markingFileId) return;
    setMarkingFileId(fileId);
    try {
      await updateContractFile(contractId, fileId, { isExecuted: true });
      showToast('Marked as executed', 'success');
      refreshContract();
    } catch (err) {
      showToast(err.message || 'Failed to mark as executed', 'error');
    } finally {
      setMarkingFileId(null);
    }
  };

  const openFileEdit = (file) => {
    setEditingFileId(file.fileId);
    setFileDraft({
      documentDate: file.documentDate || '',
      direction: file.direction || '',
      fileNotes: file.fileNotes || '',
    });
  };

  const cancelFileEdit = () => {
    setEditingFileId(null);
    setFileDraft({ documentDate: '', direction: '', fileNotes: '' });
  };

  const saveFileMeta = async (fileId) => {
    if (savingFileMeta) return;
    setSavingFileMeta(true);
    try {
      await updateContractFile(contractId, fileId, {
        documentDate: fileDraft.documentDate || null,
        direction: fileDraft.direction || null,
        fileNotes: fileDraft.fileNotes || null,
      });
      showToast('File updated', 'success');
      cancelFileEdit();
      refreshContract();
    } catch (err) {
      showToast(err.message || 'Failed to save', 'error');
    } finally {
      setSavingFileMeta(false);
    }
  };

  return {
    cancelFileEdit,
    closeFileDeleteConfirm,
    deleteContractDriveFolderWithLastFile,
    deleteContractWithLastFile,
    deleteDriveFile,
    deletingFileId,
    editingFileId,
    fileDeleteTarget,
    fileDraft,
    handleDeleteFile,
    handleMarkAsExecuted,
    markingFileId,
    openFileDeleteConfirm,
    openFileEdit,
    saveFileMeta,
    savingFileMeta,
    setDeleteContractDriveFolderWithLastFile,
    setDeleteContractWithLastFile,
    setDeleteDriveFile,
    setFileDraft,
  };
}
